import {
  FileText,
  Image,
  LucideIcon,
  RefreshCw,
  Rocket,
  Star,
  Tag,
  Trash2,
} from 'lucide-react';
import { appStoreAppURL, type PublisherEvent, type PublisherEventType } from '../api/client';

export function publisherEventIcon(type: PublisherEventType): { Icon: LucideIcon; className: string } {
  switch (type) {
    case 'new_app':
      return { Icon: Rocket, className: 'bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400' };
    case 'removed_app':
      return { Icon: Trash2, className: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' };
    case 'app_updated':
      return { Icon: RefreshCw, className: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' };
    case 'rating_changed':
      return { Icon: Star, className: 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400' };
    case 'price_changed':
      return { Icon: Tag, className: 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400' };
    case 'metadata_changed':
      return { Icon: FileText, className: 'bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-400' };
    case 'visual_changed':
      return { Icon: Image, className: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400' };
    default:
      return { Icon: RefreshCw, className: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' };
  }
}

/** Single timeline row — shows ready-to-display `message` from the API as-is. */
export function PublisherEventListItem({
  event,
  storefront,
}: {
  event: PublisherEvent;
  storefront?: string;
}) {
  const { Icon, className } = publisherEventIcon(event.eventType);
  return (
    <li className="px-4 py-3 flex items-start gap-3">
      <span
        className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full shrink-0 ${className}`}
        aria-hidden
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <PublisherEventMessage event={event} storefront={storefront} />
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          {new Date(event.createdAt).toLocaleString()}
        </p>
      </div>
    </li>
  );
}

/** Wrap the app name in a store link for new_app events; other types stay plain text. */
function PublisherEventMessage({
  event,
  storefront,
}: {
  event: PublisherEvent;
  storefront?: string;
}) {
  const name = event.appName.trim();
  if (event.eventType !== 'new_app' || !name || !event.appTrackId) {
    return <>{event.message}</>;
  }
  const idx = event.message.lastIndexOf(name);
  if (idx < 0) {
    return <>{event.message}</>;
  }
  return (
    <>
      {event.message.slice(0, idx)}
      <a
        href={appStoreAppURL(storefront ?? '', event.appTrackId)}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
      >
        {name}
      </a>
      {event.message.slice(idx + name.length)}
    </>
  );
}
